import { BehaviorSubject, combineLatest, Observable, switchMap } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { ChangeDetectionStrategy, Component, Inject, Input, Optional } from '@angular/core';

import { RuleAction } from '@app/client/criterion-addition/shared/models/rule.models';
import {
  getSubscriptionDiscount,
  isLegacySubscription,
  isPrimordialSubscription,
} from '@app/client/settings/shared/modules/subscription/subscription.utilities';
import { SwitchAnimation } from '@app/core/animations/switch.animation';
import {
  AdspertPlanType,
  BillingVolumeType,
  Currency,
  CustomerFeature,
  CustomerFeatureStatus,
  CustomerSubscription,
  InvoiceCurrency,
  PlanPriceVolumePercentageTier,
  SubscriptionInvoicePeriod,
} from '@app/core/models/customer.models';
import { ActionsState } from '@app/core/store/states/actions/actions.state';
import { CustomerState } from '@app/core/store/states/customer/customer.state';
import { PreviewCustomerSubscription } from '@app/core/store/states/customer/subscription/subscription-state.actions';
import { SubscriptionState } from '@app/core/store/states/customer/subscription/subscription.state';
import { shareRef } from '@app/shared/operators/share-ref.operator';
import { Store } from '@ngxs/store';
import { NZ_MODAL_DATA } from 'ng-zorro-antd/modal';

@Component({
  standalone: false,
  selector: 'adspt-pricing-details',
  templateUrl: 'pricing-details.component.html',
  styleUrls: ['pricing-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [SwitchAnimation],
})
export class PricingDetailsComponent {
  readonly preloading$: Observable<boolean> = this.store.select(ActionsState.whileAction(PreviewCustomerSubscription));

  readonly checkoutCurrency$: Observable<InvoiceCurrency> = this.store.select(SubscriptionState.checkoutCurrency);

  readonly hasSubscription$: Observable<boolean> = this.store.select(SubscriptionState.hasSubscription);

  readonly isAgency$: Observable<boolean> = this.store.select(CustomerState.customerIsAgency);

  readonly userCurrency$: Observable<Currency> = this.store.select(CustomerState.userCurrency);

  readonly invoiceCurrencies$: Observable<InvoiceCurrency[]> = this.store.select(SubscriptionState.checkoutCurrencies);

  readonly subscription$ = new BehaviorSubject<CustomerSubscription | null>(null);

  readonly isLegacySubscription$ = this.subscription$.pipe(
    map((subscription) => isLegacySubscription(subscription) || isPrimordialSubscription(subscription)),
  );

  readonly updateInvoiceCurrency$ = new BehaviorSubject<InvoiceCurrency | null>(null);

  readonly updateInvoicePeriod$ = new BehaviorSubject<SubscriptionInvoicePeriod | null>(null);

  readonly updatePlanType$ = new BehaviorSubject<AdspertPlanType | null>(null);

  readonly invoiceCurrency$: Observable<InvoiceCurrency> = combineLatest([
    this.subscription$,
    this.checkoutCurrency$,
    this.userCurrency$,
    this.updateInvoiceCurrency$,
  ]).pipe(
    map(
      ([subscription, checkoutCurrency, userCurrency, invoiceCurrency]) =>
        <InvoiceCurrency>(invoiceCurrency || subscription?.currency || checkoutCurrency || userCurrency || 'EUR'),
    ),
    shareRef(),
  );

  readonly subscriptionInvoicePeriod$: Observable<SubscriptionInvoicePeriod> = this.subscription$.pipe(
    map((subscription) => subscription?.renewalMonthsCurrent),
    shareRef(),
  );

  readonly invoicePeriod$: Observable<SubscriptionInvoicePeriod> = combineLatest([
    this.subscriptionInvoicePeriod$,
    this.updateInvoicePeriod$,
  ]).pipe(
    map(
      ([subscriptionInvoicePeriod, invoicePeriod]) =>
        invoicePeriod || subscriptionInvoicePeriod || SubscriptionInvoicePeriod.Monthly,
    ),
    shareRef(),
  );

  readonly previewSubscriptions$: Observable<CustomerSubscription[]> = this.invoiceCurrency$.pipe(
    distinctUntilChanged(),
    switchMap((currency) => this.store.dispatch(new PreviewCustomerSubscription(currency))),
    switchMap(() => this.store.select(SubscriptionState.preview)),
    shareRef(),
  );

  readonly plans$: Observable<{ type: AdspertPlanType; price: number; unavailable: boolean }[]> =
    this.previewSubscriptions$.pipe(
      map((previewSubscriptions) =>
        previewSubscriptions.map((sub) => ({
          type: sub.type,
          price: sub.pricingDetails?.fixedFee,
          unavailable: sub.unavailable,
        })),
      ),
      shareRef(),
    );

  readonly planType$: Observable<AdspertPlanType> = combineLatest([
    this.subscription$,
    this.updatePlanType$,
    this.previewSubscriptions$,
    this.plans$,
    this.isAgency$,
  ]).pipe(
    map(
      ([subscription, planType, previewSubscriptions, plans, isAgency]) =>
        planType ||
        (plans.some((plan) => plan.type === subscription?.type)
          ? subscription.type
          : previewSubscriptions.find(
              (plan) => !plan.unavailable && plan.features.every((feature) => feature.available || !feature.inUse),
            )?.type) ||
        (isAgency ? AdspertPlanType.Scale : AdspertPlanType.Essential),
    ),
    shareRef(),
  );

  readonly previewSubscription$: Observable<CustomerSubscription> = combineLatest([
    this.previewSubscriptions$,
    this.planType$,
  ]).pipe(
    map(([previewSubscriptions, planType]) => previewSubscriptions.find((sub) => sub.type === planType)),
    shareRef(),
  );

  readonly annualDiscountPercentage$: Observable<number> = combineLatest([
    this.subscription$,
    this.previewSubscription$,
  ]).pipe(
    map(
      ([subscription, previewSubscription]) =>
        (subscription && getSubscriptionDiscount(subscription)) || getSubscriptionDiscount(previewSubscription),
    ),
  );

  readonly percentageRanges$: Observable<PlanPriceVolumePercentageTier[]> = this.previewSubscription$.pipe(
    map(
      (previewSubscription) =>
        previewSubscription?.pricingDetails?.volumePercentage?.find(
          (volumePercentage) => volumePercentage.volumeType === BillingVolumeType.AttributedSales,
        )?.tiers || [],
    ),
    shareRef(),
  );

  readonly analyticsPrice$: Observable<number> = this.previewSubscriptions$.pipe(
    map((previewSubscriptions) =>
      previewSubscriptions.reduce((price, sub) => {
        price =
          sub.pricingDetails?.addOns?.find(
            (addOn) => addOn.featureName === CustomerFeature.MerchantSpring && addOn.fixedFee > 0,
          )?.fixedFee || price;

        return price;
      }, 0),
    ),
    shareRef(),
  );

  @Input()
  newPlan?: boolean;

  @Input()
  set subscription(subscription: CustomerSubscription) {
    this.subscription$.next(subscription);
  }

  @Input()
  set selection(
    selection: { plan: AdspertPlanType; currency?: InvoiceCurrency; period?: SubscriptionInvoicePeriod } | null,
  ) {
    this.updatePlanType$.next(selection?.plan);
    this.updateInvoiceCurrency$.next(selection?.currency);
    this.updateInvoicePeriod$.next(selection?.period);
  }

  readonly subscriptionChangeStep$ = new BehaviorSubject<'details' | 'downgrade' | 'confirmation'>('details');

  downgradeSteps: CustomerFeatureStatus[] = [];

  constructor(
    @Optional() @Inject(NZ_MODAL_DATA) data: Partial<PricingDetailsComponent>,
    private store: Store,
  ) {
    Object.keys(data || {}).forEach((key) => (this[key] = data[key]));
  }

  protected readonly SubscriptionInvoicePeriod = SubscriptionInvoicePeriod;

  protected readonly ruleAction = RuleAction;
}
